import { useContext } from "react"
import classNames from "classnames"

import DataCell from "./DataCell"
import RowSelector from "./RowSelector"
import { DataTableContext } from "./DataTable"

import { LocalizationContext, RecordContext } from "web/contexts"

export default function DataRow({ data, rowNumber, onClick }) {
  const {
    data: tableData,
    columns,
    cells,
    canSelectRow,
    onRowSelectionChanged,
    isEditable: tableIsEditable,
    hasFrozenColumns,
    showRowNumbers,
  } = useContext(DataTableContext)

  const rowIndex = tableData.findIndex((el) => el.id === data.id)

  const rowIsEven = rowNumber % 2 === 0

  const ordinalClasses = classNames(
    "datatable-data-cell border-b border-neutral-9 max-w-full flex focus:bg-primary-10 items-center p-0 justify-center w-[30px]",
    {
      "bg-primary-11": !tableIsEditable && rowIsEven,
      "bg-white": tableIsEditable || !rowIsEven,
      "sticky left-0": hasFrozenColumns,
    }
  )

  const rowSelectorClasses = classNames(
    "datatable-data-cell border-b border-neutral-9 max-w-full flex focus:bg-primary-10 items-center p-0 justify-center w-[30px]",
    {
      "bg-primary-11": !tableIsEditable && rowIsEven,
      "bg-white": tableIsEditable || !rowIsEven,
      sticky: hasFrozenColumns,
      "left-0": hasFrozenColumns && !showRowNumbers,
      "left-[30px]": hasFrozenColumns && showRowNumbers,
    }
  )

  function handleClick() {
    if (tableIsEditable) return
    onClick({ rowNumber, data })
  }

  const inheritedLocalizationContext = useContext(LocalizationContext)
  const { currency_code, area_unit_code, length_unit_code } = data

  return (
    <RecordContext.Provider value={data}>
      <LocalizationContext.Provider
        value={{
          ...inheritedLocalizationContext,
          defaultCurrency:
            currency_code || inheritedLocalizationContext.defaultCurrency,
          defaultAreaUnit:
            area_unit_code || inheritedLocalizationContext.defaultAreaUnit,
          defaultLengthUnit:
            length_unit_code || inheritedLocalizationContext.defaultLengthUnit,
        }}
      >
        <div
          data-testid="datatable-data-row"
          className="contents"
          onClick={onClick && handleClick}
        >
          {showRowNumbers && (
            <div className={ordinalClasses}>
              {data.location_ordinals?.length > 0 && (
                <span
                  title={data.location_ordinals.join(", ")}
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-neutral-6 px-1 text-2xs font-medium text-white"
                >
                  {data.location_ordinals.length > 1
                    ? `×${data.location_ordinals.length}`
                    : data.location_ordinals[0]}
                </span>
              )}
            </div>
          )}
          {canSelectRow && (
            <RowSelector
              id={rowNumber}
              className={rowSelectorClasses}
              data={data}
              selected={data.selected}
              onRowSelectionChanged={onRowSelectionChanged}
            />
          )}
          {columns.map((column, n) => {
            return (
              <DataCell
                key={n}
                data={data}
                column={column}
                rowNumber={rowNumber}
                cell={cells[rowIndex][n]}
              />
            )
          })}
        </div>
      </LocalizationContext.Provider>
    </RecordContext.Provider>
  )
}
